/**
 * Modal/folha com ENTRADA DE HISTÓRICO — o voltar do sistema fecha o modal,
 * não a página (resíduo ⑥ do BUG-71, BACKLOG §0 item 7, 24/09).
 *
 * ## O defeito
 *
 * Nenhum modal do app empilhava histórico. O voltar do Android
 * (`NativeBackButtonBridge` → `history.back()`), o gesto da borda e o botão
 * do navegador tiravam a PÁGINA de baixo do modal aberto — o que a pessoa
 * queria fechar continuava ali, sobre outra tela, ou sumia junto com a página.
 *
 * ## O mecanismo (pelo Router, nunca `history` cru)
 *
 * Abrir empilha uma entrada com o MESMO endereço e a marca do modal em
 * `location.state.__overlays`. Voltar desempilha: a marca some e o dono é
 * avisado (`onBack`). Fechar pela UI desfaz a própria entrada (`navigate(-1)`).
 * Tudo passa por `navigate` do React Router porque ele carimba `{usr, key,
 * idx}` — um `pushState`/`replaceState` cru apaga o `idx` e o `useBack` passa
 * a achar que não há histórico (invariante 8 da `ux-mechanics`).
 *
 * ## As quatro armadilhas que o desenho carrega
 *
 * 1. **Fechar e navegar no mesmo gesto** (Foco: `onClose()` + `navigate("/pro")`).
 *    O `navigate(-1)` do fechamento é ADIADO e relê o topo real do histórico
 *    antes de agir: se o topo já é outra rota, não volta — senão tiraria a
 *    pessoa do `/pro` que ela acabou de abrir.
 * 2. **StrictMode** reexecuta efeitos (`effect → cleanup → effect`). O push é
 *    travado por ref (`pendingPushRef`) e o pop adiado não é cancelado no
 *    cleanup — a reexecução acharia `ownsEntry` já falso e nunca voltaria.
 * 3. **Voltar que o dono recusa** (Foco rodando mostra o aviso de saída em vez
 *    de fechar). Depois do `onBack`, se o modal continua aberto no próximo
 *    tick, a entrada é reempilhada — senão o voltar seguinte levaria a página.
 * 4. **Entrada órfã** (fechou por navegação, recarregou a página): quem tem
 *    `onRestore` reabre ao chegar nela; o resto é limpo pelo
 *    `OverlayHistoryJanitor`, que pula a entrada vazia.
 */

import { createContext, useContext, useEffect, useId, useRef, type Context } from "react";
import * as RouterDom from "react-router-dom";
import type { Location, To } from "react-router-dom";

export const OVERLAY_STATE_KEY = "__overlays";

type State = Record<string, unknown> | null | undefined;

/** As marcas de modal presentes num `location.state`. */
export function overlaysOf(state: unknown): string[] {
  if (!state || typeof state !== "object") return [];
  const value = (state as Record<string, unknown>)[OVERLAY_STATE_KEY];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/** `state` + a marca `id` no topo (sem duplicar). Preserva o resto do state. */
export function withOverlay(state: unknown, id: string): Record<string, unknown> {
  const base = state && typeof state === "object" ? (state as Record<string, unknown>) : {};
  return { ...base, [OVERLAY_STATE_KEY]: [...overlaysOf(state).filter((v) => v !== id), id] };
}

/** `state` sem as marcas indicadas (todas, sem `ids`). */
export function withoutOverlays(state: unknown, ids?: readonly string[]): Record<string, unknown> {
  const base = state && typeof state === "object" ? { ...(state as Record<string, unknown>) } : {};
  const left = ids ? overlaysOf(state).filter((v) => !ids.includes(v)) : [];
  if (left.length > 0) base[OVERLAY_STATE_KEY] = left;
  else delete base[OVERLAY_STATE_KEY];
  return base;
}

/** O `location.state` da entrada ATUAL, lido do histórico real (React Router guarda em `usr`). */
export function currentHistoryState(): State {
  try {
    const raw = window.history.state as { usr?: State } | null;
    return raw?.usr ?? null;
  } catch {
    return null;
  }
}

/**
 * A marca `id` é a de CIMA na entrada atual — só então o fechamento pela UI
 * pode desfazer a entrada com `go(-1)`.
 *
 * ⚠️ BUG-330 (06/10): trocar uma folha por outra no MESMO gesto ("Trocar" →
 * "Escolher do pote" no clube). A folha nova empilha a entrada dela copiando o
 * `state` de baixo, que ainda tem a marca da que fecha (`[A, B]`); o pop
 * adiado da que fecha via "A está no topo" e voltava — levando a entrada de B,
 * e B fechava junto ("nada aconteceu"). Com a marca de outra por cima, a que
 * fecha não mexe na pilha: o `OverlayHistoryJanitor` tira a marca órfã desta
 * entrada e pula a de baixo quando o voltar chegar nela.
 */
function isTopOverlay(id: string): boolean {
  const marks = overlaysOf(currentHistoryState());
  return marks.length > 0 && marks[marks.length - 1] === id;
}

/**
 * Marcas que têm dono montado agora — o `OverlayHistoryJanitor` limpa as que
 * não têm. Módulo, e não contexto: dono e zelador vivem em árvores diferentes
 * (o Foco no `AppFrame`, o Studio num portal).
 */
const claimedOverlays = new Set<string>();

export function isOverlayClaimed(id: string): boolean {
  return claimedOverlays.has(id);
}

export interface OverlayHistoryOptions {
  /** O modal está aberto (e cobre a tela) agora. */
  open: boolean;
  /**
   * O voltar do sistema desempilhou a entrada. O dono fecha (ou recusa — o
   * Foco rodando mostra o aviso; aí a entrada volta sozinha).
   */
  onBack: () => void;
  /**
   * Id ESTÁVEL entre montagens — só quem quer `onRestore` precisa. Ausente =
   * `useId()` (a folha comum, que não reabre sozinha).
   */
  id?: string;
  /**
   * Chegou-se de volta a uma entrada com a marca deste modal e ele está
   * fechado (ex.: voltou do `/pro`). Reabra-o. Ausente = a entrada é pulada.
   */
  onRestore?: () => void;
}

/**
 * O Router pelos contextos, e não por `useLocation`/`useNavigate`: aqueles
 * LANÇAM fora de um Router, e a folha é primitivo de UI — renderizada em teste
 * e em superfície sem rota. Sem Router, o hook é no-op (a folha segue fechando
 * pelo ✕ como sempre). O `navigator` do `BrowserRouter` é o próprio `history`
 * do Router: `push`/`go` carimbam `{usr, key, idx}` como o `navigate` faria.
 */
interface RouterNavigator {
  push: (to: To, state?: unknown) => void;
  go: (delta: number) => void;
}

/**
 * Os contextos vêm do `react-router-dom` — é ele que o `BrowserRouter` provê.
 * ⚠️ NÃO troque por `import … from "react-router"`: medido em 24/09, no vitest
 * os dois pacotes carregam INSTÂNCIAS DIFERENTES do contexto (pacote duplo
 * CJS/ESM) e o hook vira no-op em silêncio — o mesmo poderia acontecer num
 * build. A leitura é protegida porque dezenas de testes mockam o `-dom`
 * inteiro só com `useNavigate`, e o vitest LANÇA ao acessar export ausente
 * num mock: sem o contexto, o hook usa um contexto vazio e vira no-op.
 */
const EMPTY_CONTEXT = createContext<unknown>(null);
function routerContext(get: () => unknown): Context<unknown> {
  try {
    return (get() as Context<unknown> | undefined) ?? EMPTY_CONTEXT;
  } catch {
    return EMPTY_CONTEXT;
  }
}
const LocationCtx = routerContext(() => RouterDom.UNSAFE_LocationContext);
const NavigationCtx = routerContext(() => RouterDom.UNSAFE_NavigationContext);

function useRouterBits(): { location: Location | null; navigator: RouterNavigator | null } {
  const locationCtx = useContext(LocationCtx) as { location?: Location } | null;
  const navigationCtx = useContext(NavigationCtx) as { navigator?: RouterNavigator } | null;
  return { location: locationCtx?.location ?? null, navigator: navigationCtx?.navigator ?? null };
}

const hereOf = (loc: { pathname: string; search: string; hash: string }): To => ({
  pathname: loc.pathname,
  search: loc.search,
  hash: loc.hash,
});

export function useOverlayHistory({ open, onBack, id: fixedId, onRestore }: OverlayHistoryOptions): void {
  const autoId = useId();
  const id = fixedId ?? `ov${autoId}`;
  const { location, navigator } = useRouterBits();
  const routed = location !== null && navigator !== null;

  /** A entrada deste modal existe no histórico (empilhada ou reivindicada). */
  const ownsEntryRef = useRef(false);
  /** Um push foi pedido e ainda não chegou ao `location` (trava do StrictMode). */
  const pendingPushRef = useRef(false);
  const openRef = useRef(open);
  const onBackRef = useRef(onBack);
  const onRestoreRef = useRef(onRestore);
  useEffect(() => {
    openRef.current = open;
    onBackRef.current = onBack;
    onRestoreRef.current = onRestore;
  });

  const inEntry = routed && overlaysOf(location.state).includes(id);
  const locationKey = location?.key ?? "";

  const claim = (v: boolean) => {
    ownsEntryRef.current = v;
    if (v) claimedOverlays.add(id);
    else claimedOverlays.delete(id);
  };

  // Aberto: garante a entrada; entrada sumida com o modal aberto = voltar.
  useEffect(() => {
    if (!routed || !open) return;
    if (inEntry) {
      pendingPushRef.current = false;
      claim(true);
      return;
    }
    if (pendingPushRef.current) return;
    if (ownsEntryRef.current) {
      claim(false);
      onBackRef.current();
      // Armadilha 3: o dono pode ter recusado o fechamento.
      const t = setTimeout(() => {
        if (openRef.current && !ownsEntryRef.current && !pendingPushRef.current) {
          pendingPushRef.current = true;
          navigator.push(hereOf(window.location), withOverlay(currentHistoryState(), id));
        }
      }, 0);
      return () => clearTimeout(t);
    }
    pendingPushRef.current = true;
    navigator.push(hereOf(location), withOverlay(location.state, id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a chave da entrada é o gatilho; o resto é lido fresco
  }, [open, locationKey]);

  // Fechado pela UI com a entrada ainda nossa: desfaz a entrada (adiado — armadilha 1).
  useEffect(() => {
    if (!routed || open || !ownsEntryRef.current) return;
    claim(false);
    setTimeout(() => {
      if (ownsEntryRef.current) return; // reaberto antes do tick
      if (isTopOverlay(id)) navigator.go(-1);
    }, 0);
    // Sem cleanup de propósito: ver armadilha 2.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Chegou numa entrada com a nossa marca, fechado: reabre se souber.
  useEffect(() => {
    if (!routed || open || !inEntry || ownsEntryRef.current || !onRestoreRef.current) return;
    claim(true);
    onRestoreRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey]);

  // Desmontou aberto (o dono sumiu com o modal): mesma limpeza do fechamento.
  useEffect(
    () => () => {
      if (!ownsEntryRef.current || !navigator) return;
      claim(false);
      setTimeout(() => {
        if (isTopOverlay(id)) navigator.go(-1);
      }, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
